import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { getLocale, history } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  SelectLang,
} from '@/components';
import { queryCurrentUser } from '@/services/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { fixMenus } from './services/util';
import {message} from 'antd';


const loginPath = '/user/login';
const fontFamilyMap = {
  zh: '"SimHei", "黑体", sans-serif',
  ja: '"Meiryo UI", sans-serif',
};

const syncLocaleFont = () => {
  if (typeof document === 'undefined') return;
  const fontFamily = getLocale() === 'ja-JP' ? fontFamilyMap.ja : fontFamilyMap.zh;
  document.documentElement.style.fontFamily = fontFamily;
  document.body.style.fontFamily = fontFamily;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  syncLocaleFont();
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/pwdset'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({
  initialState
}) => {
  syncLocaleFont();
  return {
    actionsRender: () => [
      <div key='title' className='c-title'>{getLocale() === 'ja-JP' ? defaultSettings.jaTitle : defaultSettings.title}</div>,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // 调用后端返回的menuData, 自定义渲染
    postMenuData: (menuData) => {
      if (!initialState?.currentUser?.is_superuser) {
        const currentUser = initialState?.currentUser;
        const permissions = currentUser?.permissions;
        const menus = fixMenus(menuData, permissions);
        return menus;
      }else{
        return menuData
      }
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
        </>
      );
    },
    logo: initialState?.currentUser?.logo || '/luosen.png',
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
  timeout: 30000,
  requestInterceptors: [
    (url, options) => {
      const isFormData = options?.data instanceof FormData;
      const headers: Record<string, string> = {
        'Authorization': 'Token ' + localStorage.getItem('token')
      }
      // FormData 上传不设置 Content-Type，让浏览器自动附加 boundary
      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }
      return {
        url: url,
        options: { ...options, headers: { ...options?.headers, ...headers } }
      }
    }
  ],
  responseInterceptors: [
    async (response) => {
      const res = response.data;
      if (res.code === 10001) {
        history.push(loginPath)
      }
      return response
    }
  ]
};

message.config({
  top: 200
})
